import type {
  MediaReference,
  Product,
  ProductColorVariant,
  ProductMedia,
  ProductQueryData,
  ProductVariant,
  VariantMedia,
  VariantQueryData,
} from "./types";

const EMPTY_MEDIA: VariantMedia = { images: [], videos: [] };

function getMetafield<T extends { namespace: string; key: string }>(
  metafields: (T | null)[] | undefined,
  key: string,
): T | undefined {
  return metafields?.find((metafield): metafield is T =>
    Boolean(metafield?.namespace === "custom" && metafield.key === key),
  );
}

function getMediaUrl(
  node: MediaReference | undefined,
  preferredVideoHeight?: number,
): ProductMedia | null {
  if (node?.__typename === "MediaImage" && node.image?.url) {
    return { type: "image", url: node.image.url };
  }

  if (node?.__typename === "Video" && node.sources?.length) {
    const source = preferredVideoHeight
      ? [...node.sources]
          .filter((candidate) => candidate.height)
          .sort(
            (a, b) =>
              Math.abs((a.height || 0) - preferredVideoHeight) -
              Math.abs((b.height || 0) - preferredVideoHeight),
          )[0] || node.sources[0]
      : node.sources[0];

    if (source?.url) {
      return {
        type: "video",
        url: source.url,
        posterUrl: node.previewImage?.url || null,
      };
    }
  }

  return null;
}

function getVariantMedia(variant: ProductVariant, key: string): VariantMedia {
  const edges = getMetafield(variant.metafields, key)?.references?.edges;
  if (!edges) return EMPTY_MEDIA;

  const images: string[] = [];
  const videos: string[] = [];

  edges.forEach(({ node }) => {
    if (node.__typename === "MediaImage" && node.image?.url) {
      images.push(node.image.url);
      return;
    }

    if (node.__typename !== "Video" || !node.sources?.length) return;

    const source =
      node.sources.find((candidate) => candidate.url.includes("1080")) ||
      [...node.sources].sort((a, b) => (b.height || 0) - (a.height || 0))[0];

    if (source?.url) videos.push(source.url);
  });

  return { images, videos };
}

function getLandingMedia(
  product: Product,
  key: string,
): ProductQueryData["landingMedia"]["desktop"] {
  const edges = getMetafield(product.metafields, key)?.references?.edges;
  const isMobile = key.endsWith("_mobile");

  return {
    backgroundMedia: getMediaUrl(edges?.[0]?.node, isMobile ? 720 : 1080),
    fullscreenVideoUrl:
      edges?.[1]?.node.__typename === "Video"
        ? edges[1].node.sources?.[0]?.url || null
        : null,
  };
}

function getExitMedia(product: Product, key: string): ProductMedia | null {
  const node = getMetafield(product.metafields, key)?.references?.edges?.[0]
    ?.node;
  return getMediaUrl(node);
}

export function buildVariantQueryData(
  variant: ProductVariant,
): VariantQueryData {
  const storeMediaEdges = getMetafield(
    variant.metafields,
    "store_media_card_new",
  )?.references?.edges;

  return {
    media: getVariantMedia(variant, "media"),
    productAestheticMedia: getVariantMedia(variant, "product_aesthetic_media"),
    primaryColor:
      getMetafield(variant.metafields, "primary_color")?.value || null,
    accentColor:
      getMetafield(variant.metafields, "accent_color")?.value || null,
    storeCardMedia: getMediaUrl(storeMediaEdges?.[0]?.node)?.url || null,
    hoverImage: getMediaUrl(storeMediaEdges?.[1]?.node)?.url || null,
  };
}

export function withVariantQueryData(variant: ProductVariant): ProductVariant {
  return { ...variant, queryData: buildVariantQueryData(variant) };
}

export function buildProductQueryData(product: Product): ProductQueryData {
  const aestheticNode = getMetafield(
    product.metafields,
    "product_aesthetic_media",
  )?.references?.edges?.[0]?.node;

  const colorVariants: Record<string, ProductColorVariant> = {};

  product.variants.forEach((variant, variantIndex) => {
    const color =
      variant.selectedOptions?.find(
        (option) => option.name.toLowerCase() === "color",
      )?.value || "Default";

    if (colorVariants[color]) return;

    const variantData = variant.queryData!;
    colorVariants[color] = {
      name: variant.title || color,
      color,
      primaryColor: variantData.primaryColor,
      accentColor: variantData.accentColor,
      video: variantData.media.videos[0] || null,
      images: variantData.media.images,
      storeCardMedia: variantData.storeCardMedia,
      hoverImage: variantData.hoverImage,
      variantIndex,
    };
  });

  return {
    colorVariants,
    landingMedia: {
      desktop: getLandingMedia(product, "hero_section_landing_media_desktop"),
      mobile: getLandingMedia(product, "hero_section_landing_media_mobile"),
    },
    exitMedia: {
      desktop: getExitMedia(product, "exit_section_media_desktop"),
      mobile: getExitMedia(product, "exit_section_media_mobile"),
    },
    aestheticMediaUrl: getMediaUrl(aestheticNode)?.url || null,
    isBestSeller:
      getMetafield(product.metafields, "product_popularity_tag")?.value ===
      "true",
    minimumPrice: getMinimumPrice(product),
  };
}

function getMinimumPrice(product: Product): number {
  const variantPrices = product.variants
    .map((variant) => Number.parseFloat(variant.price?.amount))
    .filter(Number.isFinite);

  if (variantPrices.length > 0) return Math.min(...variantPrices);

  return Number.parseFloat(product.priceRange.minVariantPrice.amount);
}

export function withProductQueryData(product: Product): Product {
  const variants = product.variants.map(withVariantQueryData);
  const normalizedProduct = { ...product, variants };

  return {
    ...normalizedProduct,
    queryData: buildProductQueryData(normalizedProduct),
  };
}
